import React, { useContext, useEffect, useState } from 'react';
import styles from './ClaimRewards.module.scss';
import TowerTokenImg from '../../assets/imgs/tower-token 2.png';
import { Web3Context } from '../../web3';
import { useTranslation } from 'react-i18next';
import { ABIContext, TYPES } from '../Context';
import { toBN } from 'web3-utils';
import { estimatePriorityFee } from '../../services/contractService';
import { GeneralModal } from '../GeneralModal';
import {fetchPayoutConfig, fetchPayoutMerkleRoot, fetchPlayerStatus} from '../../services/claimRewardsServices';
import { Button, Modal } from 'react-bootstrap';
import { PolygonNetworkInfo } from '../../services/networkServices';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import iconSuccess from '../../assets/imgs/bridge/icon-success.png';
import { NoBattleCardsOwnedNotice } from '../ViewTower/NoBattleCardsOwnedNotice';

const BLOCKPASS_URL = process.env.REACT_APP_BLOCKPASS_VERIFICATION_URL;

const ClaimRewards = ({ walletInfo, userBattleCardCount, userTicketCount }) => {
    const [t] = useTranslation();
    const [verifiedUser, setVerifiedUser] = useState(false);
    const { selectedAccount, web3, switchNetwork, connect } = useContext(Web3Context);
    const [abiConfig, abiDispatch] = useContext(ABIContext);
    const [merkleProof, setMerkleProof] = useState({
        proof: null,
        amount: 0,
        batch: 0,
        isClaimed: null
    });
    const [distributorContract, setDistributorContract] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [claimedAmount, setClaimedAmount] = useState(0);

    const createContract = async (provider, deploymentContext) => {
        try {
            const contract = new provider.eth.Contract(deploymentContext.abi, deploymentContext.address);
            return contract;
        } catch (error) {
            console.error("Error creating contracts", error);
        }
    }

    const checkUserVerified = async () => {
        let statusResponse = await fetchPlayerStatus(web3.utils.toChecksumAddress(selectedAccount));
        if (statusResponse && statusResponse?.data && statusResponse.data.status === "approved") {
            setVerifiedUser(true);
        } else {
            setVerifiedUser(false);
        }
    }

    const handleClaim = async () => {
        setLoading(true);

        let switchNetworkResponse = await switchNetwork(PolygonNetworkInfo);
        if (!switchNetworkResponse) {
            setLoading(false);
            return;
        }

        try {
            let isLocked = await distributorContract.methods.isLocked().call();
            if (isLocked) {
                setError({
                    show: true,
                    title: 'PAYOUT_LOCKED',
                    description: 'PAYOUT_LOCKED_TRY_AGAIN',
                    buttonText: 'CLOSE'
                });
                setLoading(false);
                return;
            }

            let amount = web3.utils.toWei(merkleProof.amount);

            let gasPrice = await web3.eth.getGasPrice();
            let gasPriceBN = toBN(gasPrice);
            let finalGasPrice = gasPriceBN.add(toBN(parseInt(0.1 * gasPriceBN))).toString();
            let gasEstimate = await distributorContract.methods.claimPayout(
                selectedAccount,
                amount,
                merkleProof.batch,
                merkleProof.proof
            ).estimateGas({ from: selectedAccount });

            let estimatedPriorityFee = await (await estimatePriorityFee(web3)).toString();
            let finalMaxFeePerGas = toBN(finalGasPrice).add(toBN(parseInt(estimatedPriorityFee))).toString();

            const txPromise = new Promise((resolve, reject) => {
                distributorContract.methods.claimPayout(
                    selectedAccount,
                    amount,
                    merkleProof.batch,
                    merkleProof.proof
                ).send({
                    from: selectedAccount,
                    gasLimit: gasEstimate,
                    maxFeePerGas: finalMaxFeePerGas,
                    maxPriorityFeePerGas: estimatedPriorityFee
                })
                    .on('transactionHash', (txHash) => {
                        console.log(`Transaction hash obtained : ${txHash}`);
                    })
                    .on('receipt', (receipt) => {
                        resolve(receipt);
                    })
                    .on('error', e => {
                        reject(e);
                    })
            });

            await txPromise.then( async (receipt) => {
                const transactionHash = receipt.transactionHash;
                console.log(`Transaction completed with receipt.transactionHash : ${transactionHash}`);
                setMerkleProof({
                    proof: null,
                    amount: 0,
                    batch: 0,
                    isClaimed: null
                });
                setShowSuccessModal(true);
            });
        } catch (err) {
            console.error(`Transaction failed: ${err.message}`);
            if (err.code !== 4001) {
                if (err.data?.message?.includes('Payout already claimed')) {
                    setError({
                        show: true,
                        title: 'PAYOUT_CLAIMED',
                        description: 'PAYOUT_ALREADY_CLAIMED',
                        buttonText: 'CLOSE'
                    });
                } else {
                    setError({
                        show: true,
                        title: 'SOMETHING_WENT_WRONG',
                        description: 'PLEASE_TRY_AGAIN_LATER',
                        buttonText: 'CLOSE'
                    });
                }
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        (async () => {
            if (selectedAccount && walletInfo?.WalletId?.toLowerCase() === selectedAccount?.toLowerCase()) {
                checkUserVerified();

                try {
                    const merkleRootRes = await fetchPayoutMerkleRoot(selectedAccount);
                    if (!merkleRootRes || !merkleRootRes.address) {
                        return;
                    }

                    if (merkleRootRes.isClaimed) {
                        return;
                    }

                    setMerkleProof({
                        ...merkleProof,
                        proof: merkleRootRes.proof,
                        amount: web3.utils.fromWei(toBN(merkleRootRes.amount, 16).toString()),
                        batch: merkleRootRes.batch,
                        isClaimed: merkleRootRes.isClaimed,
                    });

                    setClaimedAmount(web3.utils.fromWei(toBN(merkleRootRes.amount, 16).toString()));

                    const payoutConfig = await fetchPayoutConfig();
                    if (payoutConfig && payoutConfig?.payoutDistributor) {
                        abiDispatch({
                            type: TYPES.SET_CLAIM_REWARDS_CONFIG,
                            payload: {
                                payoutDistributor: payoutConfig.payoutDistributor
                            },
                        });
                    }
                } catch (err) {
                    console.log("Error initializing blockpass", err);
                }
            } else {
                setVerifiedUser(false);
                setMerkleProof({
                    proof: null,
                    amount: 0,
                    batch: 0,
                    isClaimed: null
                });
            }
        })();
    }, [selectedAccount])

    useEffect(() => {
        (async () => {
            setLoading(true);
            if (abiConfig.payoutDistributor) {
                let payoutDistributorContract = await createContract(web3, abiConfig.payoutDistributor);
                setDistributorContract(payoutDistributorContract);
            }
            setLoading(false);
        })();
    }, [abiConfig.payoutDistributor])

    const renderClaimPage = (
        <div className={styles.verified_section}>
            <div className={styles.xp_gain_pte_events_claimable_title}>{t('XP_GAIN_DAILY_STAR_CHEST_CLAIMABLE_TOKENS')}</div>
            <div className={styles.description}>
                <div>{t('COMPLETE_PLAYER_VERIFICATION_ASAP')}</div>
                <div>{t('STRICTLY_ENFORCE_KYC_ONWARDS')}</div>
                <ul>
                    <li>{t('ELIGIBLE_SHARE_XP_GAIN_PRIZE_POOL')}</li>
                    <li>{t('ELIGIBLE_RECEIVE_DAILY_STAR_CHEST')}</li>
                </ul>
            </div>
            <div className={styles.tokens}>
                <img src={TowerTokenImg} alt="tower-token" />
                <span>{parseFloat(merkleProof.amount) ? parseFloat(merkleProof.amount).toLocaleString() : "0"}</span>
            </div>
            <div className={styles.verification_status}>
                <div className={styles.title}>{t('PLAYER_VERIFICATION')}</div>
                {verifiedUser ?
                    <div className={styles.status}>
                        <div className={styles.status_circle}></div>
                        <div className={styles.status_text}>{t('APPROVED')}</div>
                    </div> :
                    <div className={styles.status}>
                        <div className={`${styles.status_circle} ${styles.not_verified}`}></div>
                        <div className={styles.status_text}>{t('NOT_VERIFIED')}</div>
                    </div>
                }
            </div>
        </div>
    );

    // const renderUnverifiedPage = (
    //     <div className={styles.unverified_section}>
    //         <div className={styles.verification_necessary_text}>
    //             <span>{t('PLAYER_VERIFICATION_NECESSARY_BEFORE_CLAIM')} </span>
    //             <div className={styles.new_line_block}></div>
    //             <span>{t('CLICK_BLOCKPASS_VERIFICATION_TO_PROCEED')}</span>
    //         </div>
    //         <div className={styles.verification_status}>
    //             <div className={styles.title}>{t('PLAYER_VERIFICATION')}</div>
    //             <div className={styles.status}>
    //                 <div className={`${styles.status_circle} ${styles.not_verified}`}></div>
    //                 <div className={styles.status_text}>{t('NOT_VERIFIED')}</div>
    //             </div>
    //         </div>
    //         <div className={styles.description}>{t('PLAYER_VERIFICATION_BLOCKPASS_DESCRIPTION')}</div>
    //     </div>
    // );

    return (
        <div className={styles.claim_rewards_container}>
            <div className={styles.section_title}>
            <NoBattleCardsOwnedNotice battleCardsCount={userBattleCardCount} userTicketCount={userTicketCount}/>

                <div className={styles.title_text_daily}>
                    <span className={styles.title}>{t('CLAIM_REWARDS')}</span>
                </div>
            </div>
            {renderClaimPage}

            <div className={styles.verification_claim_buttons}>
                {selectedAccount ?
                    (walletInfo?.WalletId?.toLowerCase() !== selectedAccount?.toLowerCase()) ?
                        <div className={styles.wrong_wallet_text}>{t('WRONG_WALLET_PLEASE_SWITCH_LINKED_WALLET')}</div> :
                        <>
                            <a href={BLOCKPASS_URL} rel="noopener noreferrer" target="_blank">
                                <button
                                    className={`${styles.btn} ${verifiedUser ? styles.disabled : ''}`}
                                    disabled={verifiedUser}
                                >{t('BLOCKPASS_VERIFICATION')}</button>
                            </a>
                            <div className={styles.dots}>
                                <div className={styles.dot}></div>
                                <div className={styles.dot}></div>
                                <div className={styles.dot}></div>
                            </div>
                            <button
                                className={`${styles.btn} ${!loading && parseFloat(merkleProof.amount) > 0 ? '' : styles.disabled}`}
                                disabled={loading || !(parseFloat(merkleProof.amount) > 0)}
                                onClick={handleClaim}
                            >{loading ? t('LOADING') : t('CLAIM')}</button>
                        </> :
                    <Button variant="cdh-general-blue"
                        className={styles.connect_btn}
                        onClick={connect}
                    >{t('CONNECT_WALLET')}</Button>
                }

            </div>
            
            <Modal show={showSuccessModal} className={styles.success_modal} centered>
                <div className={styles.close_btn} onClick={()=> {setShowSuccessModal(false)}}><FontAwesomeIcon icon={faTimes} /></div>
                <Modal.Body>
                    <div className={styles.modal_body}>
                        <img src={iconSuccess} alt="checkmark" className={styles.loader_img} />
                        <div className={styles.body_text}>{t('YOU_HAVE_SUCCESSFULLY_CLAIMED')} {claimedAmount} {t('TOWER')}.</div>
                        <Button className={styles.close_button} onClick={() => setShowSuccessModal(false)}>{t('OK')}</Button>
                    </div>
                </Modal.Body>
            </Modal>

            {error &&
                <GeneralModal content={{
                        show: error.show,
                        showSetter: () => setError(null),
                        title: t(error.title),
                        description: t(error.description),
                        buttonText: t(error.buttonText),
                        buttonCallback: () => setError(null)
                    }}
                />
            }
        </div>
    )
}

export { ClaimRewards };
